import React from 'react';
import pt from 'prop-types';

import { View } from 'components/view';
import Alert from 'components/alert';
import Modal from 'components/modal';
import Tabs from 'components/tabs';
import { TabView } from 'components/form';
import { SectionGroup } from 'components/section';
import CollectionEmpty from 'components/collection/empty';
import ButtonLink from 'components/button/link';
import Link from 'components/link';
import AppIcon from 'components/apps/icon';
import Icon from 'components/icon';
import Help from 'components/tooltip/help';
import Status from 'components/status';
import DateTime from 'components/date-time';
import { formatCurrencyRounded, formatDate } from 'utils';

import swellLogo from 'components/logo/swell-purple.svg';
import stripeLogo from 'components/settings/logos/stripe@2x.png';

import './earnings.scss';

const { CLIENT_URL } = process.env;

// Earnings can come from sources other than app sales
const EARNING_TYPE_LABELS = {
  app: 'App',
  referral: 'Referral',
};

export default class EarningsPage extends React.PureComponent {
  static propTypes = {
    location: pt.object.isRequired,
    earnings: pt.object,
    payouts: pt.object,
    onboarding: pt.bool,
    setupModal: pt.string,
    payoutModal: pt.object,
    payoutEarnings: pt.object,
    onClickSetupPayouts: pt.func.isRequired,
    onClickContinueToStripe: pt.func.isRequired,
    onCloseSetupModal: pt.func.isRequired,
    onClickPayout: pt.func.isRequired,
    onClosePayoutModal: pt.func.isRequired,
    managingPayouts: pt.bool,
    onClickManagePayouts: pt.func.isRequired,
    accountModal: pt.bool,
    accountDetails: pt.object,
    accountLoading: pt.bool,
    confirmDisconnect: pt.bool,
    disconnecting: pt.bool,
    onCloseAccountModal: pt.func.isRequired,
    onClickStripeDashboard: pt.func.isRequired,
    onClickDisconnect: pt.func.isRequired,
    onCancelDisconnect: pt.func.isRequired,
    onConfirmDisconnect: pt.func.isRequired,
  };

  // A payout is only scheduled when payouts are set up and the payable
  // balance meets the minimum
  hasScheduledPayout() {
    const summary = this.props.payouts?.summary || {};
    const minimum = summary.payout_minimum ?? 0;

    return Boolean(
      summary.date_payout_scheduled && (summary.payable || 0) >= minimum,
    );
  }

  getPayoutScheduleMessage() {
    const summary = this.props.payouts?.summary || {};
    const minimum = summary.payout_minimum;

    const minimumText = minimum
      ? ` once your payable earnings reach ${formatCurrencyRounded(minimum)}`
      : '';

    return `Payouts are sent automatically once a month${minimumText}. Earnings below the minimum roll over to the following month.`;
  }

  renderSetupBrands() {
    return (
      <div className="earnings-setup-brands">
        <span className="earnings-setup-brand">
          <img src={swellLogo} alt="Swell" />
        </span>
        <Icon fa="plus" className="earnings-setup-plus" />
        <span className="earnings-setup-brand">
          <img src={stripeLogo} alt="Stripe" />
        </span>
      </div>
    );
  }

  renderSetupModal() {
    const {
      setupModal,
      onboarding,
      onClickContinueToStripe,
      onCloseSetupModal,
    } = this.props;

    if (setupModal === 'connected') {
      return (
        <Modal
          title="Payouts connected"
          width={480}
          cancel={false}
          onClose={onCloseSetupModal}
          actions={[{ label: 'Done', onClick: onCloseSetupModal }]}
        >
          {this.renderSetupBrands()}
          <p>
            Your Stripe account is connected. Your payable earnings will be
            included in the next monthly payout.
          </p>
        </Modal>
      );
    }

    if (setupModal === 'waiting') {
      return (
        <Modal
          title="Set up payouts"
          width={480}
          onClose={onCloseSetupModal}
          actions={[]}
        >
          <p>
            Finish connecting your account in the Stripe window. This page
            updates automatically when you&apos;re done.
          </p>
        </Modal>
      );
    }

    if (setupModal === 'verifying') {
      return (
        <Modal
          title="Verifying your account"
          width={480}
          onClose={onCloseSetupModal}
          actions={[]}
          cancelText="Close"
        >
          <p>
            Your details have been submitted and Stripe is verifying your
            account. This usually takes a minute or two.
          </p>
          <p>
            You can close this window &mdash; payouts will be enabled
            automatically as soon as verification is complete.
          </p>
        </Modal>
      );
    }

    return (
      <Modal
        title="Set up payouts"
        width={480}
        onClose={onCloseSetupModal}
        actions={[
          {
            label: 'Continue to Stripe',
            onClick: onClickContinueToStripe,
            disabled: onboarding,
          },
        ]}
      >
        {this.renderSetupBrands()}
        <p>
          Swell uses <b>Stripe</b> to send your app earnings directly to your
          bank account. You&apos;ll continue to Stripe to verify your business
          details and connect a bank account.
        </p>
        <p>
          Swell never sees or stores your banking information &mdash; it is held
          securely by Stripe.
        </p>
      </Modal>
    );
  }

  renderPayoutModal() {
    const { payoutModal, payoutEarnings, onClosePayoutModal } = this.props;
    const results = payoutEarnings?.results || [];

    return (
      <Modal
        title="Payout details"
        width={640}
        cancel={false}
        onClose={onClosePayoutModal}
        actions={[{ label: 'Done', onClick: onClosePayoutModal }]}
        loading={!payoutEarnings}
      >
        <p>
          {formatDate(payoutModal.date_created, 'longExact')}
          <br />
          <b>
            {formatCurrencyRounded(payoutModal.amount, payoutModal.currency)}
          </b>
        </p>
        {results.length > 0 && (
          <div className="collection-table-container account-next-invoice-table">
            <table className="collection-table white-space-wrap">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Source</th>
                  <th>Sale</th>
                  <th className="currency">Your earnings</th>
                </tr>
              </thead>
              <tbody>
                {results.map((earning) => (
                  <tr key={earning.id}>
                    <td>
                      {formatDate(earning.date_created, 'monthDayShortComma')}
                    </td>
                    <td>{this.renderEarningSource(earning)}</td>
                    <td>{formatCurrencyRounded(earning.price)}</td>
                    <td className="currency">
                      {formatCurrencyRounded(earning.payout_total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    );
  }

  renderEarningType(earning) {
    return EARNING_TYPE_LABELS[earning.type] || EARNING_TYPE_LABELS.app;
  }

  renderEarningSource(earning) {
    if (earning.app) {
      return (
        <Link
          to={`/apps/${earning.app.id}`}
          className="earnings-source collection-table-link"
        >
          <AppIcon
            image={earning.app.logo_icon}
            name={earning.app.name}
            size={24}
          />
          {earning.app.name}
        </Link>
      );
    }

    const { client } = earning;

    if (client) {
      return (
        <Link
          to={CLIENT_URL.replace('CLIENT_ID', client.id)}
          target="blank"
          className="earnings-source collection-table-link"
        >
          {client.name || client.id}
        </Link>
      );
    }

    return <div className="earnings-source">{earning.description || '—'}</div>;
  }

  renderEarningStatus(earning) {
    if (earning.payout_id) {
      return <Status type="positive">Paid out</Status>;
    }

    if (earning.paid) {
      return <Status type="warning">Payable</Status>;
    }

    return <Status type="muted">Pending</Status>;
  }

  renderEarnings() {
    const { earnings } = this.props;
    const results = earnings?.results || [];

    if (results.length === 0) {
      return (
        <CollectionEmpty
          emptyTitle="No earnings yet"
          emptyDescription="When stores purchase your published apps, your earnings will appear here"
          emptyAction={false}
        />
      );
    }

    return (
      <div className="collection-table-container">
        <table className="collection-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Source</th>
              <th />
              <th>Sale</th>
              <th>Fee</th>
              <th>Status</th>
              <th className="currency">Your earnings</th>
            </tr>
          </thead>
          <tbody>
            {results.map((earning) => (
              <tr key={earning.id}>
                <td>
                  <DateTime value={earning.date_created} format="age" />
                </td>
                <td>{this.renderEarningType(earning)}</td>
                <td>{this.renderEarningSource(earning)}</td>
                <td>{formatCurrencyRounded(earning.price)}</td>
                <td>{formatCurrencyRounded(earning.fee)}</td>
                <td>{this.renderEarningStatus(earning)}</td>
                <td className="currency">
                  {formatCurrencyRounded(earning.payout_total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  renderAccountModal() {
    const {
      accountDetails,
      accountLoading,
      confirmDisconnect,
      disconnecting,
      managingPayouts,
      onCloseAccountModal,
      onClickStripeDashboard,
      onClickDisconnect,
      onCancelDisconnect,
      onConfirmDisconnect,
    } = this.props;

    if (confirmDisconnect) {
      return (
        <Modal
          title="Disconnect payout account?"
          width={480}
          cancel={false}
          onClose={onCancelDisconnect}
          actions={[
            {
              label: 'Disconnect',
              type: 'danger',
              onClick: onConfirmDisconnect,
              disabled: disconnecting,
            },
            { label: 'Cancel', type: 'cancel', onClick: onCancelDisconnect },
          ]}
        >
          <p>
            Payouts will stop until you connect another account. Your payable
            earnings are kept and will be included in a future payout.
          </p>
        </Modal>
      );
    }

    const details = accountDetails || {};
    const hasDetails = Object.keys(details).length > 0;

    return (
      <Modal
        title="Payout account"
        width={520}
        cancel={false}
        onClose={onCloseAccountModal}
        loading={accountLoading}
        actions={[
          {
            label: 'Manage in Stripe',
            onClick: onClickStripeDashboard,
            disabled: managingPayouts,
          },
          {
            label: 'Disconnect',
            type: 'secondary danger',
            onClick: onClickDisconnect,
          },
          { label: 'Close', type: 'cancel', onClick: onCloseAccountModal },
        ]}
      >
        {this.renderSetupBrands()}
        {!hasDetails && (
          <p className="muted">
            Account details are unavailable right now. You can still manage or
            disconnect your account.
          </p>
        )}
        {hasDetails && (
          <table className="collection-table account-details-table">
            <tbody>
              {details.business_name && (
                <tr>
                  <td>Business</td>
                  <td>{details.business_name}</td>
                </tr>
              )}
              {details.bank && (
                <tr>
                  <td>Bank account</td>
                  <td>
                    {details.bank.name}
                    {details.bank.last4 ? ` •••• ${details.bank.last4}` : ''}
                  </td>
                </tr>
              )}
              {details.email && (
                <tr>
                  <td>Email</td>
                  <td>{details.email}</td>
                </tr>
              )}
              {(details.country || details.currency) && (
                <tr>
                  <td>Country</td>
                  <td>
                    {[details.country, details.currency]
                      .filter(Boolean)
                      .join(' · ')}
                  </td>
                </tr>
              )}
              <tr>
                <td>Status</td>
                <td>
                  {details.payouts_enabled ? (
                    <Status type="positive">Payouts enabled</Status>
                  ) : details.requirements_due > 0 ? (
                    <Status type="warning">Information needed</Status>
                  ) : (
                    <Status type="muted">Verifying</Status>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        )}
        <p className="muted">
          Bank details and tax information are managed securely in Stripe.
        </p>
      </Modal>
    );
  }

  renderPayoutSettings() {
    const { managingPayouts, onClickManagePayouts } = this.props;
    const summary = this.props.payouts?.summary || {};

    if (!summary.details_submitted) {
      return null;
    }

    return (
      <div className="box earnings-payout-settings">
        <div className="box-section">
          <div className="box-grid">
            <div className="box-column">
              <div className="box-title">Payout account</div>
              <div className="box-subtitle">
                {summary.enabled
                  ? 'Payouts are sent to your connected Stripe account.'
                  : 'Stripe is verifying your account details.'}
              </div>
            </div>
            <div className="box-column earnings-payout-settings-action">
              <ButtonLink
                type="secondary"
                size="sm"
                onClick={onClickManagePayouts}
                disabled={managingPayouts}
              >
                Manage in Stripe
              </ButtonLink>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderPayouts() {
    const { payouts } = this.props;
    const results = payouts?.results || [];

    if (results.length === 0) {
      const { onClickSetupPayouts } = this.props;
      const summary = this.props.payouts?.summary || {};

      return (
        <CollectionEmpty
          emptyTitle="No payouts yet"
          emptyDescription="Payouts are sent monthly once your payable earnings reach the minimum"
          emptyAction={
            summary.enabled || summary.details_submitted
              ? false
              : 'Set up payouts'
          }
          onClickAction={onClickSetupPayouts}
        />
      );
    }

    return (
      <div className="collection-table-container">
        <table className="collection-table collection-table-hoverable">
          <thead>
            <tr>
              <th>Date</th>
              <th className="currency">Amount</th>
              <th>Earnings</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {results.map((payout) => (
              <tr
                key={payout.id}
                data-id={payout.id}
                onClick={this.props.onClickPayout}
              >
                <td>{formatDate(payout.date_created, 'monthDayShortComma')}</td>
                <td className="currency">
                  {formatCurrencyRounded(payout.amount, payout.currency)}
                </td>
                <td>{payout.earnings_count}</td>
                <td>
                  {payout.status === 'paid' ? (
                    <Status type="positive">Paid</Status>
                  ) : payout.status === 'failed' ? (
                    <Status type="negative">Failed</Status>
                  ) : (
                    <Status type="muted">Pending</Status>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const { location, payouts, onboarding, onClickSetupPayouts } = this.props;
    const tab = location.query.tab || 'earnings';
    const summary = payouts?.summary || {};

    return (
      <div className="earnings">
        <View full headerTitle="Earnings">
          {!summary.enabled &&
            (summary.details_submitted ? (
              <Alert type="info">
                Stripe is verifying your account details. Payouts will be
                enabled automatically once verification is complete.
              </Alert>
            ) : (
              <Alert type="info">
                {/* Wrap text + action in one element so the alert's flex gap
                    doesn't separate them — they read as a single line. */}
                <span>
                  Set up payouts to receive your earnings.{' '}
                  <button
                    className="as-link"
                    type="button"
                    disabled={onboarding}
                    onClick={onClickSetupPayouts}
                  >
                    {summary.account_created
                      ? 'Continue payout setup'
                      : 'Set up payouts'}
                  </button>
                </span>
              </Alert>
            ))}
          <SectionGroup>
            <table className="kpi-table">
              <tbody>
                <tr>
                  <td>
                    <span className="kpi-label">Payable</span>
                    <span className="kpi-value">
                      <div>{formatCurrencyRounded(summary.payable || 0)}</div>
                    </span>
                  </td>
                  <td>
                    <span className="kpi-label">Pending</span>
                    <span className="kpi-value">
                      <div>{formatCurrencyRounded(summary.pending || 0)}</div>
                    </span>
                  </td>
                  <td>
                    <span className="kpi-label">Paid out</span>
                    <span className="kpi-value">
                      <div>
                        {formatCurrencyRounded(summary.total_paid || 0)}
                      </div>
                    </span>
                  </td>
                  <td width="25%">
                    <span className="kpi-label">
                      Next payout
                      <Help
                        message={this.getPayoutScheduleMessage()}
                        maxTextLineWidth={48}
                      />
                    </span>
                    <span className="kpi-value">
                      <div>
                        {this.hasScheduledPayout()
                          ? formatDate(
                              summary.date_payout_scheduled,
                              'monthDayShortComma',
                            )
                          : '—'}
                      </div>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </SectionGroup>
          <div className="view-header-tabs">
            <Tabs
              items={{
                earnings: {
                  label: 'Earnings',
                },
                payouts: {
                  label: 'Payouts',
                },
              }}
              location={location}
              active={tab}
            />
          </div>
          <TabView value={tab} active="earnings">
            {this.renderEarnings()}
          </TabView>
          <TabView value={tab} active="payouts">
            {this.renderPayoutSettings()}
            {this.renderPayouts()}
          </TabView>
          {this.props.setupModal && this.renderSetupModal()}
          {this.props.payoutModal && this.renderPayoutModal()}
          {this.props.accountModal && this.renderAccountModal()}
        </View>
      </div>
    );
  }
}
